import { useState, useEffect } from 'react';

import { GraphQLResponse, useGraphQLDebug } from '@/fo/api/magento';
import {
  Product,
  ReviewsData,
  ReviewsFilter
} from '@/fo/ui/review/catalog/base';

import { getReviews, ReviewsReturn } from '../api/getReviews';

const INITIAL_PAGE = 1;

export function useReviews(product: Product, initialReviewsData: ReviewsData) {
  const [reviewsData, setReviewsData] = useState(initialReviewsData);
  const [reviewsPage, setReviewsPage] = useState(INITIAL_PAGE);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [reviewsFilters, setReviewsFilters] = useState<ReviewsFilter>({
    selectedRating: null,
    selectedTags: []
  });
  const [debugResponse, setDebugResponse] = useState<
    GraphQLResponse<ReviewsReturn> | undefined
  >();

  useGraphQLDebug(debugResponse, 'getReviews');

  const updateDebugResponse = (response: ReviewsReturn) => {
    if (response.debugInfo) {
      setDebugResponse({
        data: response,
        statusCode: 200,
        debugInfo: response.debugInfo
      });
    }
  };

  const fetchInitialReviews = async () => {
    const response = await getReviews(product.id, reviewsPage, reviewsFilters);

    updateDebugResponse(response);
    setReviewsData(response.reviews);
    setShowLoadMore(response.pageInfo.totalPages > reviewsPage);
  };

  useEffect(() => {
    fetchInitialReviews();
  }, []);

  const handleFiltersChange = async (filters: ReviewsFilter) => {
    setReviewsPage(INITIAL_PAGE);
    setReviewsLoading(true);

    setReviewsFilters(filters);

    const response = await getReviews(product.id, INITIAL_PAGE, filters);

    updateDebugResponse(response);
    setReviewsData(response.reviews);
    setShowLoadMore(response.pageInfo.totalPages > reviewsPage);
    setReviewsLoading(false);
  };

  const fetchMoreReviews = async () => {
    setReviewsLoading(true);

    const response = await getReviews(
      product.id,
      reviewsPage + 1,
      reviewsFilters
    );

    updateDebugResponse(response);

    setReviewsData((prev) => {
      const allItems = [...prev.items, ...response.reviews.items];
      const uniqueMap = new Map(allItems.map((item) => [item.id, item]));
      const uniqueItems = Array.from(uniqueMap.values());
      return {
        ...response.reviews,
        items: uniqueItems
      };
    });

    setShowLoadMore(response.pageInfo.totalPages > reviewsPage + 1);

    setReviewsPage(reviewsPage + 1);
    setReviewsLoading(false);
  };

  return {
    reviewsData,
    reviewsLoading,
    fetchMoreReviews,
    handleFiltersChange,
    showLoadMore
  };
}
